'use strict';

import React from 'react';
import { createRoot } from 'react-dom/client';

import CourseSummaryWidget from './CourseSummaryWidget';
import { isTrue } from '@uw-it-sis/lib-react/lib/Utils';

/**
 * Utility methods that deals with creating links and parsing "linkified" DOM elements etc.
 */
const LinkBuilder = {};

/**
 * Parses a block of raw text which contains "linkified" spans and replaces them with links to the
 * appropriate Course Details page (which is currently in the MyPlan application).
 * Any text nodes are put in React span elements.
 *
 * @param text The raw text to parse.
 * @param link if true, elements that are parsed as links instead of as raw text
 * @returns A React div element which contains spans of text and/or link elements.
 */
LinkBuilder.linkify = function(text, link) {
    /* Parse the raw HTML into elements. */
    const parser = new DOMParser();
    const tags = /<(\w*)\s(.*?)['"\d\w\s]\/>/g;
    const tmp = parser.parseFromString(text.replace(tags, '<$1 $2></$1>'), "text/html");
    const elements = tmp.body.childNodes;
    let parsedElements = [];

    if (elements) {
        for (let i = 0; i < elements.length; i++) {
            let element = elements[i];
            /*
             * If the node is a span and has the linkified CSS class then make a link to the MyPlan CDP.
             * Otherwise, wrap the text in a span.
             *
             * We may need to rethink this if we content with other
             * HTML elements in it. This code assumes that we'll only ever see spans that need to be turned
             * into links to the CDP.
             */
            if ( element.nodeName.toLowerCase() === "span" && element.classList.contains("linkified") ) {
                let text = element.getAttribute("data-text");

                if ( isTrue(link) ) {
                    let subject = element.getAttribute("data-subject");
                    let number = element.getAttribute("data-number");
                    let title = element.getAttribute("data-title");

                    let href = _buildCourseHref(subject, number);

                    parsedElements[i] = <a key={i} title={title} href={href}>{text}</a>;
                } else {
                    parsedElements[i] = <span key={i}>{text}</span>;
                }
            } else {
                parsedElements[i] = <span key={i}>{element.textContent}</span>;
            }
        }
    }

    return parsedElements;
};

/**
 * Replaces all "linkified" span elements with CourseSummaryWidgets within the given DOM element.
 * @param e A dom element which contains some "linkified" spans.
 * @param analyticsContext The key that will be used when creating analytics events.
 */
LinkBuilder.linkifyDocument = function(e, analyticsContext) {
/*
 *  isPlanAudit is being passed here so CourseSummaryWidget and CourseSummary are aware of
 *  their Plan vs Degree audit contexts for analytics logging. However, this is a hindrance
 *  for sharing this component beyond Audit app. We tried to pass it through 'context', however,
 *  Linkified components are not in the subtree as far as React hierarchy is concerned
 *
 */

    let elements = e.getElementsByClassName("linkified");

    for (let i = 0; i < elements.length; i++) {
        let element = elements[i];
        let course = {
            "id": element.getAttribute("data-id"),
            "subject": element.getAttribute("data-subject"),
            "number": element.getAttribute("data-number"),
            "title": element.getAttribute("data-title"),
            "text": element.getAttribute("data-text"),
            "courseCode": `${element.getAttribute("data-subject")} ${element.getAttribute("data-number")}`
        };

        createRoot(element).render(<CourseSummaryWidget course={course} analyticsContext={analyticsContext} />);
    }
};

/**
 * Builds an authentication-service-provider-appropriate HRef to the MyPlan Course Details page.
 *
 * @param subject The subject area of the course code (e.g. CHEM, ENGL).
 * @param number The course number of the course code (e.g. 101, 250)
 */
const _buildCourseHref = function(subject, number) {
    /*
     * This will build a course href to course details page like /course/#/courses/COM 210
     */
    return `/course/#/courses/${subject} ${number}`;
};

export default LinkBuilder;